import {
  Box,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  CircularProgress,
  Tooltip,
  FormHelperText
} from '@mui/material';
import { useEffect, useRef } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MaskInput } from '@shared/common/MaskInput';
import Scrollbar from '@shared/components/Scrollbar';
import {
  OsiTrustedUsersWidgetViewModel,
  OsiTrustedUsersWidgetViewModelToken,
  AddTrustedUserFormValues
} from '../model/ViewModel';
import { addTrustedUserSchema } from '../config/validationSchema';

// Права доступа
const ACCESS_OPTIONS = [
  { value: false, label: 'Полные права (Редактирование)' },
  { value: true, label: 'Только просмотр' }
];

// Маска для казахстанского номера телефона
const PHONE_MASK = '+7 (000) 000-00-00';

// Значения по умолчанию
const defaultValues: AddTrustedUserFormValues = {
  phone: '',
  note: '',
  isReadOnly: true
};

export const OsiTrustedUsersWidget: React.FC = observer(() => {
  const vm = useInjection<OsiTrustedUsersWidgetViewModel>(OsiTrustedUsersWidgetViewModelToken);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<AddTrustedUserFormValues>({
    defaultValues,
    resolver: yupResolver(addTrustedUserSchema),
    mode: 'onBlur'
  });

  // Флаг «добавление прошло успешно — сбросить форму, когда handleSubmit завершится».
  // Сбрасывать прямо в onSubmit нельзя: onSubmit выполняется ВНУТРИ handleSubmit, и уже после
  // него react-hook-form выставляет isSubmitted = true, перетирая сброс. Затем IMaskInput,
  // получив пустое value, вызывает onAccept → field.onChange(''), а при isSubmitted = true
  // срабатывает reValidateMode: 'onChange' → поле «Номер телефона» сразу горит
  // «Введите номер телефона». Поэтому сброс переносим в эффект после окончания отправки
  // (рекомендованный RHF способ: reset после завершения submit, а не внутри него).
  const resetAfterSuccessRef = useRef(false);

  useEffect(() => {
    if (!isSubmitting && resetAfterSuccessRef.current) {
      resetAfterSuccessRef.current = false;
      // reset без keep-опций очищает isSubmitted/touched/errors — форма как при первом открытии
      reset(defaultValues);
    }
  }, [isSubmitting, reset]);

  // Обработка отправки формы
  const onSubmit = async (data: AddTrustedUserFormValues) => {
    const success = await vm.addTrustedUser(data);
    // При ошибке сервера форму не трогаем — введённые данные остаются
    resetAfterSuccessRef.current = success;
  };

  // Удаление доверенного пользователя
  const handleDelete = async (userPhone: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить доверенного пользователя?')) {
      return;
    }
    await vm.deleteTrustedUser(userPhone);
  };

  // Получение текста прав доступа
  const getAccessLabel = (readOnly: boolean): string => {
    return readOnly ? 'Только просмотр' : 'Полные права';
  };

  if (!vm.osiId) {
    return (
      <Card sx={{ maxWidth: 1000, minWidth: 800 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, minWidth: 800 }}>
      {/* Форма добавления */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Typography variant="h5" color="textPrimary">
              Добавить доверенного пользователя
            </Typography>
          }
        />
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="isReadOnly"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.isReadOnly}>
                      <InputLabel id="access-rights-label">Права доступа</InputLabel>
                      <Select
                        {...field}
                        labelId="access-rights-label"
                        label="Права доступа"
                      >
                        {ACCESS_OPTIONS.map((option) => (
                          <MenuItem key={String(option.value)} value={option.value as any}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.isReadOnly && (
                        <FormHelperText>{errors.isReadOnly.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <MaskInput
                      {...field}
                      mask={PHONE_MASK}
                      label="Номер телефона"
                      size="small"
                      placeholder="+7 (___) ___-__-__"
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Примечание"
                      placeholder="ФИО"
                      error={!!errors.note}
                      helperText={errors.note?.message}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isSubmitting ? 'Добавление...' : 'Добавить'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Таблица доверенных пользователей */}
      <Card>
        <CardHeader
          title={
            <Typography variant="h5" color="textPrimary">
              Доверенные пользователи
            </Typography>
          }
        />
        {vm.isLoading ? (
          <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </CardContent>
        ) : !Array.isArray(vm.trustedUsers) || vm.trustedUsers.length === 0 ? (
          <CardContent>
            <Typography color="text.secondary">
              Доверенные пользователи отсутствуют
            </Typography>
          </CardContent>
        ) : (
          <CardContent>
            {/* @ts-expect-error not typed component */}
            <Scrollbar>
              <TableContainer component={Box}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ФИО</TableCell>
                      <TableCell>Телефон</TableCell>
                      <TableCell>Права доступа</TableCell>
                      <TableCell align="center">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vm.trustedUsers.map((user) => (
                      <TableRow key={user.phone}>
                        <TableCell>{user.note || '—'}</TableCell>
                        <TableCell>{vm.formatPhoneDisplay(user.phone)}</TableCell>
                        <TableCell>{getAccessLabel(user.readOnly)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Удалить">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(user.phone)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </CardContent>
        )}
      </Card>
    </Box>
  );
});
